import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Upload, Download, Volume2, VolumeX, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZoneForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title_tc: '',
    title_en: '',
    title_sc: '',
    description_tc: '',
    description_en: '',
    description_sc: '',
    voice_url_en: '',
    voice_url_tc: '',
    voice_url_sc: '',
  });
  const [selectedExhibitIds, setSelectedExhibitIds] = useState([]);
  const [allExhibits, setAllExhibits] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [zoneCode, setZoneCode] = useState(null);

  useEffect(() => {
    fetchAllExhibits();
    if (isEdit) {
      fetchZone();
    }
  }, [id]);

  const fetchAllExhibits = async () => {
    try {
      const response = await axios.get(`${API}/exhibits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllExhibits(response.data);
    } catch (error) {
      toast.error('Failed to load exhibits');
    }
  };

  const fetchZone = async () => {
    try {
      // Use staff endpoint to get full zone data including descriptions and exhibit_ids
      const response = await axios.get(`${API}/staff/zones/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const zone = response.data;
      
      if (zone) {
        setFormData({
          title_tc: zone.title_tc || '',
          title_en: zone.title_en || '',
          title_sc: zone.title_sc || '',
          description_tc: zone.description_tc || '',
          description_en: zone.description_en || '',
          description_sc: zone.description_sc || '',
          voice_url_tc: zone.voice_url_tc || '',
          voice_url_en: zone.voice_url_en || '',
          voice_url_sc: zone.voice_url_sc || '',
          survey_url: zone.survey_url || '',
        });
        setSelectedExhibitIds(zone.exhibit_ids || []);
        if (zone.image_url) {
          // Add cache-busting timestamp to prevent showing cached old image
          const timestamp = zone.updated_at ? new Date(zone.updated_at).getTime() : Date.now();
          setImagePreview(`${API}${zone.image_url}?t=${timestamp}`);
        }
        if (zone.qr_code_url) {
          setQrCodeUrl(zone.qr_code_url);
        }
        if (zone.code) {
          setZoneCode(zone.code);
        }
      }
    } catch (error) {
      toast.error('Failed to load zone');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let zoneId = id;

      // Create or update zone
      if (isEdit) {
        await axios.put(
          `${API}/zones/${id}`,
          { ...formData, exhibit_ids: selectedExhibitIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Zone updated');
      } else {
        const response = await axios.post(
          `${API}/zones`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        zoneId = response.data.id;
        setZoneCode(response.data.code);
        
        // Update with exhibit IDs if any selected
        if (selectedExhibitIds.length > 0) {
          await axios.put(
            `${API}/zones/${zoneId}`,
            { exhibit_ids: selectedExhibitIds },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        toast.success('Zone created');
      }

      // Upload image if selected
      if (imageFile) {
        const formDataImage = new FormData();
        formDataImage.append('file', imageFile);
        await axios.post(
          `${API}/zones/${zoneId}/upload-image`,
          formDataImage,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      navigate('/staff/zones');
    } catch (error) {
      toast.error('Failed to save zone');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    if (!id) {
      toast.error('Please save the zone first');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/zones/${id}/generate-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCodeUrl(response.data.qr_code_url);
      toast.success('QR code generated');
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `zone-${id}-qr.png`;
    link.click();
  };

  const handleAutoTranslate = async () => {
    if (!formData.title_tc || !formData.description_tc) {
      toast.error('Please fill in Traditional Chinese title and description first');
      return;
    }

    setTranslating(true);
    try {
      const response = await axios.post(
        `${API}/exhibits/translate`,
        {
          title_tc: formData.title_tc,
          description_tc: formData.description_tc
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData({
        ...formData,
        title_en: response.data.title_en,
        description_en: response.data.description_en,
        title_sc: response.data.title_sc,
        description_sc: response.data.description_sc
      });

      toast.success('Translations generated successfully!');
    } catch (error) {
      toast.error('Translation failed. Please try again or fill manually.');
    } finally {
      setTranslating(false);
    }
  };

  const handlePreviewAudio = (language, text) => {
    if (!text || text.trim() === '') {
      toast.error('No description to preview');
      return;
    }

    if (playingAudio) {
      window.speechSynthesis.cancel();
      if (playingAudio === language) {
        setPlayingAudio(null);
        return;
      }
    }

    const voices = window.speechSynthesis.getVoices();
    
    const playPreview = () => {
      const utterance = new SpeechSynthesisUtterance(text);
      
      if (language === 'tc') {
        utterance.lang = 'zh-HK';
      } else if (language === 'sc') {
        utterance.lang = 'zh-CN';
      } else {
        utterance.lang = 'en-US';
      }
      
      const currentVoices = window.speechSynthesis.getVoices();
      let selectedVoice = null;
      
      if (language === 'tc') {
        selectedVoice = currentVoices.find(v => 
          v.lang.includes('zh-HK') || v.lang.includes('zh-TW')
        );
      } else if (language === 'sc') {
        selectedVoice = currentVoices.find(v => 
          v.lang.includes('zh-CN')
        );
      } else {
        selectedVoice = currentVoices.find(v => 
          v.lang.includes('en-US') || v.lang.includes('en-GB')
        );
      }
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      utterance.rate = 0.9;
      utterance.pitch = 1;
      
      utterance.onstart = () => setPlayingAudio(language);
      utterance.onend = () => setPlayingAudio(null);
      utterance.onerror = () => {
        setPlayingAudio(null);
        toast.error('Audio playback failed');
      };
      
      window.speechSynthesis.speak(utterance);
    };

    if (voices.length === 0) {
      window.speechSynthesis.onvoiceschanged = () => playPreview();
      window.speechSynthesis.getVoices();
    } else {
      playPreview();
    }
  };

  const handleRegenerateCode = async () => {
    if (!id) {
      toast.error('Please save the zone first');
      return;
    }

    if (!window.confirm('Regenerate zone code? This will change the code visitors use to access this zone.')) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/zones/${id}/regenerate-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setZoneCode(response.data.code);
      toast.success(`New code generated: ${response.data.code}`);
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  const toggleExhibit = (exhibitId) => {
    setSelectedExhibitIds(prev => 
      prev.includes(exhibitId) 
        ? prev.filter(id => id !== exhibitId)
        : [...prev, exhibitId]
    );
  };

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="zone-form-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/staff/zones')}
              variant="ghost"
              size="icon"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-heading font-bold">
              {isEdit ? 'Edit Zone' : 'Create New Zone'}
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Image Upload */}
          <div className="space-y-4">
            <Label>Zone Image</Label>
            <div className="flex flex-col gap-4">
              {imagePreview && (
                <div className="w-full max-w-md">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-64 object-cover rounded-lg border border-border"
                    data-testid="image-preview"
                  />
                </div>
              )}
              <div>
                <Input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange}
                  data-testid="image-upload-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  JPG or PNG, max 5MB
                </p>
              </div>
            </div>
          </div>

          {/* 3-Digit Code Section */}
          {zoneCode && (
            <div className="space-y-4 p-6 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-purple-900">3-Digit Zone Code</h3>
                  <p className="text-sm text-purple-700 mt-1">Visitors can use this code to access the zone</p>
                </div>
                <Button
                  type="button"
                  onClick={handleRegenerateCode}
                  variant="outline"
                  size="sm"
                  className="bg-white"
                  data-testid="regenerate-code-button"
                >
                  Regenerate
                </Button>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-white rounded-lg border-2 border-purple-300 p-6 text-center">
                  <div className="font-mono text-6xl font-bold text-primary tracking-widest" data-testid="zone-code-display">
                    {zoneCode}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Traditional Chinese */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold">繁體中文 (Traditional Chinese)</h3>
              <Button
                type="button"
                onClick={handleAutoTranslate}
                disabled={translating || !formData.title_tc || !formData.description_tc}
                variant="outline"
                size="sm"
                data-testid="auto-translate-button"
              >
                {translating ? (
                  <>
                    <span className="animate-spin mr-2">⟳</span>
                    Translating...
                  </>
                ) : (
                  <>
                    <span className="mr-2">🌐</span>
                    Auto Generate EN & SC
                  </>
                )}
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title_tc">Title *</Label>
              <Input
                id="title_tc"
                value={formData.title_tc}
                onChange={(e) => setFormData({ ...formData, title_tc: e.target.value })}
                required
                data-testid="title-tc-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description_tc">Description *</Label>
                <Button
                  type="button"
                  onClick={() => handlePreviewAudio('tc', formData.description_tc)}
                  variant="ghost"
                  size="sm"
                  disabled={!formData.description_tc}
                  data-testid="preview-audio-tc"
                >
                  {playingAudio === 'tc' ? (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Preview Audio
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description_tc"
                value={formData.description_tc}
                onChange={(e) => setFormData({ ...formData, description_tc: e.target.value })}
                rows={4}
                required
                data-testid="description-tc-input"
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor="voice_url_tc">Voice URL *</Label>
              <Input
                id="voice_url_tc"
                value={formData.voice_url_tc}
                onChange={(e) => setFormData({ ...formData, voice_url_tc: e.target.value })}
                required
                data-testid="voice-url-tc-input"
              />
            </div>
          </div>

          {/* English */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <h3 className="font-heading font-semibold">English</h3>
            <div className="space-y-2">
              <Label htmlFor="title_en">Title</Label>
              <Input
                id="title_en"
                value={formData.title_en}
                onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                data-testid="title-en-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description_en">Description</Label>
                <Button
                  type="button"
                  onClick={() => handlePreviewAudio('en', formData.description_en)}
                  variant="ghost"
                  size="sm"
                  disabled={!formData.description_en}
                  data-testid="preview-audio-en"
                >
                  {playingAudio === 'en' ? (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Preview Audio
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description_en"
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                rows={4}
                data-testid="description-en-input"
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor="voice_url_en">Voice URL *</Label>
              <Input
                id="voice_url_en"
                value={formData.voice_url_en}
                onChange={(e) => setFormData({ ...formData, voice_url_en: e.target.value })}
                required
                data-testid="voice-url-en-input"
              />
            </div>
          </div>

          {/* Simplified Chinese */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <h3 className="font-heading font-semibold">简体中文 (Simplified Chinese)</h3>
            <div className="space-y-2">
              <Label htmlFor="title_sc">Title</Label>
              <Input
                id="title_sc"
                value={formData.title_sc}
                onChange={(e) => setFormData({ ...formData, title_sc: e.target.value })}
                data-testid="title-sc-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description_sc">Description</Label>
                <Button
                  type="button"
                  onClick={() => handlePreviewAudio('sc', formData.description_sc)}
                  variant="ghost"
                  size="sm"
                  disabled={!formData.description_sc}
                  data-testid="preview-audio-sc"
                >
                  {playingAudio === 'sc' ? (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Preview Audio
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description_sc"
                value={formData.description_sc}
                onChange={(e) => setFormData({ ...formData, description_sc: e.target.value })}
                rows={4}
                data-testid="description-sc-input"
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor="voice_url_sc">Voice URL *</Label>
              <Input
                id="voice_url_sc"
                value={formData.voice_url_sc}
                onChange={(e) => setFormData({ ...formData, voice_url_sc: e.target.value })}
                required
                data-testid="voice-url-sc-input"
              />
            </div>
          </div>

          {/* Link Exhibits Section */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold">Link Exhibits to Zone</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Select exhibits to include in this zone ({selectedExhibitIds.length} selected)
                </p>
              </div>
            </div>
            
            {allExhibits.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No exhibits available. Create some exhibits first.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {allExhibits.map((exhibit) => {
                  const isSelected = selectedExhibitIds.includes(exhibit.id);
                  return (
                    <button
                      key={exhibit.id}
                      type="button"
                      onClick={() => toggleExhibit(exhibit.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`exhibit-select-${exhibit.id}`}
                    >
                      {exhibit.image_url ? (
                        <img
                          src={`${API}${exhibit.image_url}?t=${exhibit.updated_at ? new Date(exhibit.updated_at).getTime() : Date.now()}`}
                          alt={exhibit.title_tc}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                          No img
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{exhibit.title_tc}</div>
                        <div className="text-xs text-muted-foreground">Code: {exhibit.code}</div>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? 'border-primary bg-primary text-white'
                          : 'border-muted-foreground/30'
                      }`}>
                        {isSelected && <Check className="w-4 h-4" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Survey Section */}
          <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold">Survey</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter a URL to link a survey for this zone (e.g. Google Forms, Microsoft Forms). This will be shown to visitors after they finish exploring the zone.
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="survey_url">URL</Label>
              <Input
                id="survey_url"
                value={formData.survey_url}
                onChange={(e) => setFormData({ ...formData, survey_url: e.target.value })}
                data-testid="survey-url-input"
              />
            </div>
          </div>

          {/* QR Code Section */}
          {isEdit && (
            <div className="space-y-4 p-6 bg-white border border-border rounded-lg">
              <h3 className="font-heading font-semibold">QR Code</h3>
              {qrCodeUrl ? (
                <div className="flex items-start gap-4">
                  <div className="p-4 bg-white border border-border rounded-lg">
                    <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32" data-testid="qr-code-preview" />
                  </div>
                  <Button
                    type="button"
                    onClick={downloadQRCode}
                    variant="outline"
                    data-testid="download-qr-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR Code
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  onClick={handleGenerateQR}
                  variant="outline"
                  data-testid="generate-qr-button"
                >
                  Generate QR Code
                </Button>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="px-8"
              data-testid="save-zone-button"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Zone' : 'Create Zone'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/staff/zones')}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ZoneForm;
